import Vue from 'vue';
import { apolloProvider, httpApolloProvider } from '@/graphql/vueApollo'; // GraphQL connection
import EventBus from '@/utilities/eventBus';
import logger from 'client-logger';
import formatGraphQlError from '@/utilities/formatGraphQlError';
import { FORBIDDEN, APPLICATION_GET_FAILED } from '@/utilities/errorCodes';
// Queries and Mutations
import UPDATE_COMPANY_APPLICATION from '@/graphql/mutations/updateCompanyApplication.gql';
import GET_APPLICATIONS_BY_STATUS from '@/graphql/queries/getApplicationsByStatus.gql';
import GET_APPLICATION_BY_USER_ID from '@/graphql/queries/getApplicationByUserId.gql';
import APPROVE_APPLICATION from '@/graphql/mutations/approveApplication.gql';
import DENY_APPLICATION from '@/graphql/mutations/denyApplication.gql';

//Error Handling
import AppState from '@/store/AppState';

const apollo = apolloProvider.defaultClient;
const httpApollo = httpApolloProvider.defaultClient; // Only for operations with uploads

const Application = new Vue({
	data() {
		return {
			allApplications: {}, // List of all applications
			statusFetchRecord: {},
			userApplicationId: 0 // Will hold the user's application if fetched
		};
	},

	computed: {
		/**
		 * Fetches an application by its userId
		 * @returns {Application} - the application object
		 */
		userApplication() {
			if (!this.userApplicationId) {
				apollo
					.query({
						query: GET_APPLICATION_BY_USER_ID,
						fetchPolicy: 'network-only'
					})
					.then(data => {
						const response = data.data.getApplicationByUserId;

						// Cache it in our internal map
						this.$set(this, 'userApplicationId', response.application.id);
						this.setApplication(this.userApplicationId, response.application);
						this.setUserPaymentMethod(response.paymentMethod);
					})
					.catch(e => {
						const formattedError = AppState.showGeneralError(e);

						if (formattedError) {
							logger[formattedError.error.code === FORBIDDEN ? 'warn' : 'error'](
								{ error: (formattedError && formattedError.error) || e },
								'Failed to get application by userId'
							);

							if (formattedError.error.code === APPLICATION_GET_FAILED) {
								// Set a flag to know whether to show an error
								this.$set(this, 'userApplicationId', null);
							}
						} else {
							logger.error({ error: e }, 'Failed to get application by userId');
						}
					});
			}

			return this.allApplications[this.userApplicationId];
		},
		applicationsByStatus() {
			return status => {
				if (!this.statusFetchRecord[status]) {
					apollo
						.query({
							query: GET_APPLICATIONS_BY_STATUS,
							fetchPolicy: 'network-only',
							variables: {
								status
							}
						})
						.then(response => {
							// Record that we have already fetched this type
							this.setStatusFlag(status, true);

							const applications = response.data.getApplicationsByStatus;
							for (const app of applications) {
								this.setApplication(app.id, app);
							}
						})
						.catch(e => {
							const formattedError = formatGraphQlError(e);
							if (formattedError) {
								logger[formattedError.code === FORBIDDEN ? 'warn' : 'error'](
									{ error: formattedError, status },
									'Failed to get applications'
								);

								// Record that we have already fetched this type (only if forbidden)
								if (formattedError.code === FORBIDDEN) {
									this.setStatusFlag(status, true);
								}

								if (formattedError.code === APPLICATION_GET_FAILED) {
									// Set a flag to know whether to show an error
									this.setStatusFlag(status, null);
								}
							} else {
								logger.error({ error: e, status }, 'Failed to get applications');
							}
						});
				}

				const response = Object.values(this.allApplications).filter(app => app.status === status);
				return response;
			};
		}
	},
	methods: {
		// This sets a record entry that we have successfully queried for a given type; or tells we need to fetch again
		setStatusFlag(status, flagValue) {
			this.$set(this.statusFetchRecord, status, flagValue);
		},

		// This merges the application by id in the application dictionary
		setApplication(id, application) {
			const existing = this.allApplications[id] || {};
			this.$set(this.allApplications, id, { ...existing, ...application });
		},

		// This updates the user's payment method with the new data
		setUserPaymentMethod(paymentMethod) {
			const userApplication = this.allApplications[this.userApplicationId];
			const existing = userApplication.paymentMethod || {};

			this.$set(this.allApplications[this.userApplicationId], 'paymentMethod', { ...existing, ...paymentMethod });
		},

		async updateCompanyApplication(appJson) {
			try {
				const mutation = {
					mutation: UPDATE_COMPANY_APPLICATION,
					variables: {
						appJson
					}
				};

				const response = await httpApollo.mutate(mutation);

				const application = response.data.updateCompanyApplication;

				// Update this.userApplication
				this.setApplication(this.userApplicationId, application);

				return { data: application };
			} catch (e) {
				const formattedError = AppState.showGeneralError(e);
				logger.error({ error: (formattedError && formattedError.error) || e, appJson }, 'Error trying to update company application');
				return formattedError;
			}
		},

		/**
		 * Approve a pending application
		 * @param {Int} applicationId - the ID of the application to approve
		 * @param {String} hubspotId - the ID of the hubspot primary contact
		 * @returns {Object} - generic response object with errors and result
		 */
		async approveApplication(applicationId, hubspotId) {
			// Define the mutation
			const mutation = {
				mutation: APPROVE_APPLICATION,
				variables: {
					applicationId,
					hubspotId
				}
			};

			try {
				const {
					data: { approveApplication: response }
				} = await apollo.mutate(mutation);

				// Update our cached internal map
				if (response.result === 'Successfully approved application!') {
					// Get the application details, change the status, and add to cached approved list
					this.setApplication(applicationId, { status: 'approved' });
				}
				return { data: response };
			} catch (e) {
				const formattedError = AppState.showGeneralError(e);
				logger.error({ error: (formattedError && formattedError.error) || e, applicationId }, 'Error trying to approve company application');
				return formattedError;
			}
		},

		/**
		 * Deny a pending application
		 * @param {Int} applicationId - the ID of the application to deny
		 * @returns {Object} - generic response object with errors and result
		 */
		async denyApplication(applicationId) {
			// Define the mutation
			const mutation = {
				mutation: DENY_APPLICATION,
				variables: {
					applicationId
				}
			};

			try {
				const {
					data: { denyApplication: response }
				} = await apollo.mutate(mutation);

				// Update our cached internal map
				if (response.result === 'Successfully denied application.') {
					// Get the application details, change the status, and add to cached approved list
					this.setApplication(applicationId, { status: 'denied' });
				}

				return { data: response };
			} catch (e) {
				const formattedError = AppState.showGeneralError(e);
				logger.error({ error: (formattedError && formattedError.error) || e, applicationId }, 'Error trying to deny company application');
				return formattedError;
			}
		},

		subscriptionHandler(payload) {
			switch (payload.action) {
				case 'create': {
					// TODO: will be changed soon
					const updatedApplication = payload.application;
					this.setApplication(updatedApplication.id, updatedApplication);

					break;
				}
				case 'update': {
					const updatedApplication = payload.application;
					this.setApplication(updatedApplication.id, updatedApplication);

					break;
				}
				default: {
					logger.error({ error: `Unhandled socket: ${JSON.stringify(payload)}` }, 'Unhandled socket response');
				}
			}
		},

		// Wipes everything out that we have available directly through our props
		clearAll() {
			this.$set(this, 'allApplications', {});
			this.$set(this, 'statusFetchRecord', {});
			this.$set(this, 'userApplicationId', 0);
		}
	},
	created() {
		EventBus.$on('application', this.subscriptionHandler);
	}
});

export default Application;
